import { mdiImageAlbum } from '@mdi/js'
import Icon from '@mdi/react'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import FormControlLabel from '@mui/material/FormControlLabel'
import { FacebookAlbumSelector } from 'components/Composer/AlbumSelector'
import { PPDialog } from 'components/PPDialog'
import { Fragment, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { ComposerFacebookPost, FBAlbum, PostDestination } from 'shared/types'
import styles from './FacebookAlbumsPicker.pcss'

interface FacebookAlbumsPickerProps {
  post: ComposerFacebookPost
  profiles: PostDestination[]
  selectedAlbums: Record<string, FBAlbum | undefined>
  onSelectedAlbumsChange: (albums: Record<string, FBAlbum | undefined>, applyToAll: boolean) => void
}

export function FacebookAlbumsPicker(props: FacebookAlbumsPickerProps) {
  const [albumsPopupActive, setAlbumsPopupActive] = useState(false)
  const [applyToAll, setApplyToAll] = useState(false)
  const [selection, setSelection] = useState<Record<string, FBAlbum | undefined>>(props.selectedAlbums)

  useEffect(() => {
    setSelection(props.selectedAlbums)
  }, [props.selectedAlbums])

  const openAlbumsPopup = () => {
    setAlbumsPopupActive(true)
  }

  const closeAlbumsPopup = () => {
    setAlbumsPopupActive(false)
    setApplyToAll(false)
  }

  const toggleApplyToAll = () => {
    setApplyToAll(!applyToAll)
  }

  const confirmSelection = () => {
    props.onSelectedAlbumsChange(selection, applyToAll)
    closeAlbumsPopup()
  }

  const onSelectedAlbumChange = (profile: PostDestination, album: FBAlbum | undefined) => {
    setSelection(current => ({ ...current, [profile.ppid]: album }))
  }

  return (
    <Fragment>
      <Chip
        label={
          <FormattedMessage
            id={props.post.albums.length === 0 ? 'composer.labels.fb-album-default' : 'composer.labels.fb-album-custom'}
          />
        }
        avatar={<Icon path={mdiImageAlbum} size="20px" />}
        classes={{ root: styles.chip, label: styles.label }}
        onClick={openAlbumsPopup}
      />
      <PPDialog
        open={albumsPopupActive}
        onClose={closeAlbumsPopup}
        size="lg"
        title={<FormattedMessage id="composer.labels.select-multiple-albums" />}
        footer={
          <div className={styles.actions}>
            <div className={styles.cb}>
              <FormControlLabel
                label={<FormattedMessage id="general.labels.apply-to-all" />}
                classes={{ label: styles.label }}
                control={(
                  <Checkbox
                    checked={applyToAll}
                    color="primary"
                    size="small"
                    onChange={toggleApplyToAll}
                  />
                )}
              />
            </div>
            <Button onClick={closeAlbumsPopup}>
              <FormattedMessage id="actions.cancel" />
            </Button>
            <Button color="primary" variant="contained" onClick={confirmSelection}>
              <FormattedMessage id="actions.confirm" />
            </Button>
          </div>
        }
      >
        <div className={styles['popup-body']}>
          <div className={styles['albums-popup-content']}>
            {props.profiles.map(profile => (
              <div className={styles['option-box']} key={profile.id}>
                <FacebookAlbumSelector
                  profile={profile}
                  raised
                  selectedAlbumsCustom={[selection[profile.ppid]].filter(Boolean) as FBAlbum[]}
                  onSelectedAlbumChangeCustom={onSelectedAlbumChange}
                />
              </div>
            ))}
          </div>
        </div>
      </PPDialog>
    </Fragment>
  )
}
